import React from 'react';

import { Box, HStack, Text } from '@rbilabs/universal-components';
import { EnvItem } from 'features/diagnostics/components/env-item';
import { useStoreDiagnostics } from 'features/store/hooks/use-store-diagnostics';

import { IRestaurant } from '@rbi-ctg/store';
import { ModalContent, ModalHeading } from 'components/modal';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';

const ContentContainer = ModalContent.withConfig({
  paddingX: {
    base: '2',
    md: '15%',
  },
  paddingTop: {
    base: '0',
    md: '1',
  },
  paddingBottom: {
    md: '8',
  },
});

const StyledModalHeading = ModalHeading.withConfig({
  marginBottom: '6',
});

const SectionTitle = Text.withConfig({
  fontWeight: 'bold',
  fontSize: 'md',
});

export const determineStoreInfoSubheader = (
  storeName: string | undefined,
  storeAddress: string | undefined
) => {
  if (!storeName) {
    return `(${storeAddress})`;
  }

  if (!storeAddress || storeName.toLowerCase() === storeAddress.toLowerCase()) {
    return `(${storeName})`;
  }

  return `(${storeName} - ${storeAddress})`;
};

export const DiagnosticModalContents = ({ store }: { store: IRestaurant }) => {
  const isDiagnosticsEnabled = useFlag(LaunchDarklyFlag.ENABLE_INTERNAL_DIAGNOSTIC_TOOLS);
  const getStoreDiagnostics = useStoreDiagnostics();
  const diagnostics = getStoreDiagnostics(store);

  if (!isDiagnosticsEnabled || !diagnostics) {
    return (
      <ContentContainer>
        <StyledModalHeading>No Diagnostic Data Available</StyledModalHeading>
        <p>Sorry, diagnostic data is not available at this time!</p>
      </ContentContainer>
    );
  }
  const {
    storeName,
    storeStreetAddress,
    isStoreAvailableForPickup,
    storeLocalTime,
    storeServiceModeHours,
    storeServiceModeStatuses,
    storeDayPartHours,
    storeHasMobileOrdering,
    storeId,
    storePosVendor,
    storeMobileOrderingStatus,
    isStoreAvailableForDelivery,
    storeAvailableDayParts,
    isMobileOrderingAvailable,
    isPOSAvailable,
    isOneMenu,
  } = diagnostics;

  return (
    <ContentContainer>
      <SectionTitle>General</SectionTitle>
      <HStack alignItems="center">
        <Box width="1/2" pr="$2">
          <EnvItem envKey={'Store Id'} value={storeId} />
        </Box>
        <Box width="1/2" pl="$2">
          <EnvItem envKey={'POS Vendor'} value={storePosVendor} />
        </Box>
      </HStack>

      <EnvItem envKey={'Store Name'} value={storeName} />
      <EnvItem envKey={'Store Street Address'} value={storeStreetAddress} />
      <EnvItem envKey={'isOneMenu'} value={isOneMenu ? 'YES' : 'NO'} />

      <Box mb="$3" />

      <SectionTitle>Availability</SectionTitle>
      <EnvItem
        envKey={'isStoreAvailableForPickup'}
        value={isStoreAvailableForPickup ? 'YES' : 'NO'}
        infoMessage="Store will show as Open in the store locator"
      />

      <EnvItem
        envKey={'isStoreAvailableForDelivery'}
        value={isStoreAvailableForDelivery ? 'YES' : 'NO'}
        infoMessage="Store will be used to deliver food if its close enough to the user"
      />
      <EnvItem
        envKey={'Available DayParts'}
        value={storeAvailableDayParts}
        infoMessage="Shows what dayParts are available"
      />
      <Box mb="$3" />

      <SectionTitle>Availability Details</SectionTitle>

      <HStack alignItems="center">
        <Box width="1/2" pr="$2">
          <EnvItem
            envKey={'hasMobileOrdering'}
            value={storeHasMobileOrdering ? 'YES' : 'NO'}
            infoMessage="This means this store can accept mobile orders"
          />
        </Box>
        <Box width="1/2" pl="$2">
          <EnvItem
            envKey={'mobileOrderingStatus'}
            value={storeMobileOrderingStatus}
            infoMessage="If it is any of alpha, beta, live or dev the store store can accept mobile orders"
          />
        </Box>
      </HStack>

      <EnvItem
        envKey={'isMobileOrderingAvailable'}
        value={isMobileOrderingAvailable ? 'YES' : 'NO'}
        infoMessage="Combines the above flags to determine if the store store can accept mobile orders"
      />
      <EnvItem
        envKey={'isPosAvailable'}
        value={isPOSAvailable ? 'YES' : 'NO'}
        infoMessage="This may be override by restaurantAvailabilityOverride flag"
      />

      <EnvItem envKey={'serviceModeStatuses'} value={JSON.stringify(storeServiceModeStatuses)} />

      <EnvItem envKey={'Time at the store'} value={storeLocalTime} />
      <EnvItem
        envKey={'serviceModeHours'}
        value={JSON.stringify(storeServiceModeHours)}
        infoMessage="This store should be serving breakfast"
      />

      <EnvItem envKey={'Time at the store'} value={storeLocalTime} />
      <EnvItem
        envKey={'dayPartHours'}
        value={JSON.stringify(storeDayPartHours)}
        infoMessage="This store should be serving breakfast"
      />
    </ContentContainer>
  );
};
